<template>
    <AppLayout v-bind="appSharedData.getAppSidebarLayoutProps(pageMeta)">
        <div class="space-y-8">
            <div>
                <MeetingPageTabs :selectedTab="MeetingTabKeyEnum.Meeting" />
            </div>

            <div>
                <MeetingSearchFiltersCard
                    :paginatedMeetingsData="paginatedMeetingsData"
                    :meetingSearchFilters="localMeetingSearchFilters"
                    :services="localServices"
                    :companyUserAggregates="localCompanyUserAggregates"
                />
            </div>

            <div class="mt-8 space-y-12">
                <div
                    v-for="meetingsDataByDate in localMeetingsDataGroupedByDate"
                    :key="meetingsDataByDate.rawDate"
                >
                    <div>
                        <div class="mb-4">
                            <div
                                class="text-xs font-semibold uppercase text-gray-500"
                            >
                                {{ meetingsDataByDate.dayOfWeek }}
                            </div>
                            <div class="font-medium text-lg">
                                {{ meetingsDataByDate.formattedDate }}
                            </div>
                        </div>
                        <div class="space-y-2">
                            <div
                                v-for="meetingData in meetingsDataByDate.meetingsData"
                                :key="meetingData.meeting.id.value"
                            >
                                <MeetingCard
                                    :meetingData="meetingData"
                                    :companyUser="authCompanyUser"
                                    :displayTimezone="
                                        authCompanyUser.displayTimezone
                                    "
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="paginatedMeetingsData.numberOfRecords === 0">
                <Card>
                    <template #content>
                        <TableEmptyState title="No meetings found." />
                    </template>
                </Card>
            </div>
            <div v-else>
                <Pagination
                    as="a"
                    :searchResults="paginatedMeetingsData"
                    :url="urlFactory.makeMeetingsUrl(localMeetingSearchFilters)"
                />
            </div>
        </div>

        <PageBottomPadding />
    </AppLayout>
</template>

<script setup lang="ts">
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import { ref } from 'vue'
    import AppLayout from '@/Shared/Layout/App/Sidebar/AppLayout.vue'
    import Card from '@/Shared/Component/Card.vue'
    import {
        MeetingTabKeyEnum,
        type MeetingsDataGroupedByDate,
    } from '@/Shared/AutoGenerated/Types'
    import Pagination from '@/Shared/Component/Pagination.vue'
    import TableEmptyState from '@/Shared/Component/Table/TableEmptyState.vue'
    import MeetingCard from '@/App/Booking/Meeting/Component/MeetingCard.vue'
    import {
        type MeetingSearchFiltersType,
        type PaginatedResultsType,
        type PageMeta,
        type MeetingsGroupedByDateRawData,
    } from '@/Shared/AutoGenerated/Main'
    import Meeting from '@/App/Booking/Meeting/Entity/Meeting'
    import AppSharedData from '@/Shared/Entity/AppSharedData'
    import PageBottomPadding from '@/Shared/Layout/App/Sidebar/PageBottomPadding.vue'
    import MeetingSearchFiltersCard from '@/App/Booking/Meeting/Component/MeetingSearchFiltersCard.vue'
    import MeetingPageTabs from '@/App/Booking/Meeting/Component/MeetingPageTabs.vue'
    import Service from '@/App/Service/Entity/Service'
    import CompanyUserAggregate from '@/App/CompanyUser/Entity/CompanyUserAggregate'
    import MeetingData from '@/App/Booking/Meeting/Entity/MeetingData'

    interface IndexMeetingsProps {
        pageMeta: PageMeta
        appSharedDataData: any
        servicesData: any
        companyUserAggregatesData: any
        paginatedMeetingsData: PaginatedResultsType
        meetingSearchFilters: MeetingSearchFiltersType
    }

    const props = defineProps<IndexMeetingsProps>()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const authCompanyUser = appSharedData.getAuthCompanyUser()
    const localMeetingSearchFilters = ref(props.meetingSearchFilters)
    const localMeetingsDataGroupedByDate = ref<MeetingsDataGroupedByDate[]>([])
    const localServices = ref(Service.hydrateMultiple(props.servicesData))
    const localCompanyUserAggregates = ref(
        CompanyUserAggregate.hydrateMultiple(props.companyUserAggregatesData),
    )

    localMeetingsDataGroupedByDate.value =
        props.paginatedMeetingsData.records.map(
            (rawMeetingsData: MeetingsGroupedByDateRawData) => {
                return {
                    rawDate: rawMeetingsData.rawDate,
                    formattedDate: rawMeetingsData.formattedDate,
                    dayOfWeek: rawMeetingsData.dayOfWeek,
                    meetingsData: MeetingData.hydrateMultiple(
                        rawMeetingsData.meetingsDataRawData,
                    ),
                }
            },
        )
    const urlFactory = new UrlFactory()
</script>
